import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { Formik } from 'formik';
import { Alert, Button, Form, Modal } from 'react-bootstrap';

import { useAppDispatch, useAppSelector } from 'hooks';
import { useAxios } from 'configs/requests';
import { Input } from 'components/Forms';
import { Error, Loading } from 'components/Fetch';
import { useLogout } from 'hooks';
import { GroupManagementTabs } from 'constants/enums';
import { AccessRoles, OAuthCode } from 'constants/enums';
import { getPath } from 'routes';
import { createMarkup } from 'utils/Strings';
import { tfaValidated } from 'store/oauth/reducer';

import { postLoginRoute } from './postLoginRoute';

const TfaAuth = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.oauth.data);
  const { logout } = useLogout();

  const [tfaCode, setTfaCode] = useState<any>(user?.code);

  const [{ data: dataValidate, loading: loadingValidate, error: errorValidate }, validateTfaCode] =
    useAxios(
      {
        url: '/clinician-gateway/auth/validate-tfa-code',
        method: 'POST'
      },
      { manual: true }
    );

  const [{ data: dataNewCode }, requestNewTfaCode] = useAxios(
    {
      url: '/clinician-gateway/auth/request-new-tfa-code',
      method: 'POST'
    },
    { manual: true }
  );

  const [{ data: dataPushPhoneNumber, loading: loadingPushPhoneNumber }, pushTfaPhoneNumber] =
    useAxios(
      {
        url: '/clinician-gateway/auth/push-tfa-phone-number',
        method: 'POST'
      },
      { manual: true }
    );

  const initialValues = {
    tfaCode: ''
  };

  const tfaPhoneInitialValues = {
    tfaPhoneNumber: ''
  };

  const handleTfaCode = (formData: any) => {
    const data = {
      tfaCode: formData.values.tfaCode
    };
    validateTfaCode({ data }).then((response) => {
      if (response.data?.result === 'ok') {
        // Releases the dashboard's fetch gate. DashboardWrapper withholds the access token while
        // `oauth.data.code` still says a factor is outstanding, and the login's 67/68 sits there
        // until something clears it - which is this.
        dispatch(tfaValidated());
        // This branch never knew about billing admins, so one arriving through TFA landed on the
        // dashboard while the same person arriving by password landed on Billing.
        navigate(postLoginRoute(user?.roles || [], user?.role));
      }
    });
  };

  const handleTfaPhoneNumber = (formData: any) => {
    const data = {
      tfaPhoneNumber: formData.values.tfaPhoneNumber
    };
    pushTfaPhoneNumber({ data }).then((response) => {
      if (response.data?.result === 'ok') {
        setTfaCode(response.data.code);
      }
    });
  };

  const getNewTfaCode = () => {
    requestNewTfaCode();
  };

  return (
    <>
      <Modal
        dialogClassName="w-auto modal-dialog-centered"
        show={String(tfaCode) === OAuthCode.TFA_CODE_REQUIRED}
      >
        <Modal.Header className="">
          <Modal.Title>{intl.formatMessage({ id: 'tfaAuthenticationRequiredKey' })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{intl.formatMessage({ id: 'enterCodeSentKey' })}</p>
          <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
              handleTfaCode({ values });
            }}
          >
            {({ handleSubmit, isValid }) => {
              return (
                <Form autoComplete="off" onSubmit={handleSubmit}>
                  <Form.Group className="form-group" controlId="tfaCode">
                    <Input
                      type="text"
                      name="tfaCode"
                      placeholder={intl.formatMessage({ id: 'codeKey' })}
                      required
                    />
                  </Form.Group>
                  {errorValidate && <Error />}
                  {/* Compared as strings: the gateway sends the 7000's codes, which are string
                      constants ("81"), so coercing the enum to a number here matched nothing and
                      these alerts never rendered - a wrong code just did nothing at all. */}
                  {(String(dataValidate?.code) === OAuthCode.TFA_CODE_NOT_FOUND ||
                    String(dataValidate?.code) === OAuthCode.TFA_CODE_NO_MATCH) && (
                    <Alert variant="danger">
                      {intl.formatMessage({ id: 'tfaCodeNotFoundKey' })}
                    </Alert>
                  )}
                  {String(dataValidate?.code) === OAuthCode.TFA_CODE_EXPIRED && (
                    <Alert variant="danger">
                      {intl.formatMessage({ id: 'tfaCodeExpiredKey' })}
                    </Alert>
                  )}
                  {dataValidate?.result === 'ok' && (
                    <Alert variant="success">{intl.formatMessage({ id: 'connectedKey' })}</Alert>
                  )}
                  {dataNewCode?.result === 'ok' && (
                    <Alert variant="success">{intl.formatMessage({ id: 'codeResentKey' })}</Alert>
                  )}

                  <a onClick={getNewTfaCode} className="ms-1 cursor-pointer">
                    {intl.formatMessage({ id: 'didNotGetTFAcodeKey' })}
                  </a>
                  <hr></hr>

                  <Button className="mt-2 mb-3 float-right" variant="primary" type="submit">
                    {loadingValidate ? <Loading /> : intl.formatMessage({ id: 'submitKey' })}
                  </Button>
                </Form>
              );
            }}
          </Formik>
          {!loadingValidate && (
            <Button
              className="mt-2 mb-3 float-left"
              variant="secondary"
              type="button"
              onClick={logout}
            >
              {intl.formatMessage({ id: 'Cancel' })}
            </Button>
          )}
        </Modal.Body>
      </Modal>
      <Modal
        dialogClassName="w-auto modal-dialog-centered"
        show={String(tfaCode) === OAuthCode.TFA_PHONE_MISSING}
      >
        <Modal.Header className="">
          <Modal.Title>{intl.formatMessage({ id: 'twoFactorAuthenticationSetupKey' })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            dangerouslySetInnerHTML={createMarkup(
              intl.formatMessage({ id: 'tfaSetupPhoneNumberKey' })
            )}
          ></p>
          <Formik
            initialValues={tfaPhoneInitialValues}
            onSubmit={(values) => {
              handleTfaPhoneNumber({ values });
            }}
          >
            {({ handleSubmit, isValid }) => {
              return (
                <Form autoComplete="off" onSubmit={handleSubmit}>
                  <Form.Group className="form-group" controlId="tfaPhoneNumber">
                    <Input
                      type="text"
                      name="tfaPhoneNumber"
                      placeholder={intl.formatMessage({ id: 'phoneNumber' })}
                      required
                    />
                  </Form.Group>
                  {dataPushPhoneNumber?.result === 'no' && (
                    <Alert variant="danger">
                      {intl.formatMessage({ id: 'errorContactSupportKey' })}
                    </Alert>
                  )}
                  <hr></hr>
                  {!loadingPushPhoneNumber && (
                    /*
                     * `type="button"`, not submit. This sits inside the form and ahead of Submit,
                     * so as a submit button it was the form's *implicit submitter*: pressing Enter
                     * in the phone field ran Cancel's onClick and logged the user out, landing them
                     * back on the login page without ever calling push-tfa-phone-number (dev01,
                     * 2026-09-16). Cancel does not submit anything -- it is a button.
                     */
                    <Button
                      className="mt-2 mb-3 float-left"
                      variant="secondary"
                      type="button"
                      onClick={logout}
                    >
                      {intl.formatMessage({ id: 'Cancel' })}
                    </Button>
                  )}

                  <Button className="mt-2 mb-3 float-right" variant="primary" type="submit">
                    {loadingPushPhoneNumber ? <Loading /> : intl.formatMessage({ id: 'submitKey' })}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TfaAuth;
